'use client'

import { useEffect, useState } from 'react'
import { useMainService } from '@repo/api-client'
import { CrmApiService } from '@repo/services'
import type { ComboboxOption } from '@repo/ui'

export type CrmLookupKind = 'queue' | 'member' | 'tag' | 'channel'

interface CacheEntry {
  data: ComboboxOption[] | null
  promise: Promise<ComboboxOption[]> | null
  error: unknown
}

const cache = new Map<CrmLookupKind, CacheEntry>()
const subscribers = new Map<CrmLookupKind, Set<() => void>>()

function notify(kind: CrmLookupKind) {
  subscribers.get(kind)?.forEach((cb) => cb())
}

function entry(kind: CrmLookupKind): CacheEntry {
  let e = cache.get(kind)
  if (!e) {
    e = { data: null, promise: null, error: null }
    cache.set(kind, e)
  }
  return e
}

async function fetchLookup(api: CrmApiService, kind: CrmLookupKind): Promise<ComboboxOption[]> {
  switch (kind) {
    case 'queue': {
      const result = await api.queueList({})
      const items = (result as { items?: Array<{ id: string; name?: string }> }).items ?? []
      return items.map((it) => ({ value: it.id, label: it.name || it.id }))
    }
    case 'member': {
      const result = await api.memberList({})
      // memberList returns `{items}` with shape varying by Swift version —
      // try to surface name/email/id in that order.
      const items =
        (result as { items?: Array<Record<string, unknown>> }).items ??
        (result as { members?: Array<Record<string, unknown>> }).members ??
        []
      return items.map((m) => {
        const r = m as Record<string, unknown>
        const id = String(r['id'] ?? r['userId'] ?? '')
        const name =
          (r['displayName'] as string) ||
          (r['name'] as string) ||
          [r['firstName'], r['lastName']].filter(Boolean).join(' ') ||
          (r['email'] as string) ||
          id
        return { value: id, label: name }
      })
    }
    case 'tag': {
      const result = await api.tagList({})
      const items = (result as { items?: Array<{ id: string; name?: string }> }).items ?? []
      return items.map((it) => ({ value: it.id, label: it.name || it.id }))
    }
    case 'channel': {
      const result = await api.channelList({})
      const items = (result as { items?: Array<{ id: string; name?: string; type?: string }> }).items ?? []
      return items.map((it) => ({
        value: it.id,
        label: it.name ? `${it.name} (${it.type ?? 'channel'})` : it.id,
      }))
    }
  }
}

/**
 * Cached lookup hook for CRM entity pickers (queues, members, tags, channels).
 *
 * - Module-level cache deduplicates fetches: first caller fires the request,
 *   later callers wait on the same promise.
 * - Result stays in memory for the session lifetime; entities rarely
 *   change relative to picker usage, and the rate-limited *.list endpoints
 *   are too expensive to call per dialog open.
 * - Returns ComboboxOption[] so callers can feed directly into the UI Kit
 *   `Combobox` component.
 *
 * To force a refresh (after a create/delete from another page), call the
 * exported `invalidateCrmLookup(kind)` helper.
 */
export function useCrmLookup(kind: CrmLookupKind): {
  options: ComboboxOption[]
  isLoading: boolean
  error: unknown
} {
  const mainService = useMainService()
  const [, force] = useState(0)

  const e = entry(kind)

  useEffect(() => {
    // Subscribe to updates.
    let subs = subscribers.get(kind)
    if (!subs) {
      subs = new Set()
      subscribers.set(kind, subs)
    }
    const rerender = () => force((x) => x + 1)
    subs.add(rerender)

    // Trigger fetch if no data and no in-flight request.
    const current = entry(kind)
    if (!current.data && !current.promise) {
      const api = new CrmApiService(mainService)
      current.promise = fetchLookup(api, kind)
        .then((data) => {
          current.data = data
          current.error = null
          notify(kind)
          return data
        })
        .catch((err) => {
          current.error = err
          notify(kind)
          throw err
        })
        .finally(() => {
          current.promise = null
        })
      // Re-render so isLoading flips to true.
      rerender()
    }

    return () => {
      subs?.delete(rerender)
    }
  }, [kind, mainService])

  return {
    options: e.data ?? [],
    isLoading: !e.data && e.promise !== null,
    error: e.error,
  }
}

export function invalidateCrmLookup(kind: CrmLookupKind): void {
  const e = cache.get(kind)
  if (!e) return
  e.data = null
  e.promise = null
  e.error = null
  notify(kind)
}
